import React from 'react';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { createHttpLink } from 'apollo-link-http';
import fetch from 'isomorphic-unfetch';
import { ApolloProvider, getDataFromTree } from 'react-apollo';

import { getNodeEnv } from './env';
import Config from './Config';
import { getCookie } from './Util';
import { currentLocale } from './I18n';

// On the client, we store the Apollo Client in the following variable.
// This prevents the client from reinitializing between page transitions.
/** @type {ApolloClient<NormalizedCacheObject>?} */
let globalApolloClient = null;
/** @type {{ Authorization: string | null }?} */
let globalHeaders = null;

/**
 * @param {{ Authorization: string | null }?} headers
 * @param {string | number} clientId
 */
const createLink = (headers, clientId = Config.authClientId) => {
  const tzOffsetAndTimezone = typeof window !== 'undefined'
    ? `&tz_offset=${-(new Date().getTimezoneOffset())}&timezone=${currentLocale()}`
    : '';

  return createHttpLink({
    fetch, // Switches between unfetch & node-fetch for client & server.
    //@ts-ignore - TODO fix Config.api.uri index error
    uri: `${Config.api.uri[getNodeEnv()]}?client_id=${clientId}${tzOffsetAndTimezone}`,
    headers
  });
};

/**
 * @param {NormalizedCacheObject?} initialState
 * @param {{ Authorization: string | null }?} headers
 * @param {string | number} [clientId]
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
const createApolloClient = (initialState, headers, clientId) => new ApolloClient({
  link: createLink(headers, clientId),
  cache: new InMemoryCache()
    //  rehydrate the cache using the initial data passed from the server:
    .restore(initialState || {})
});

/**
 * @param {import('next').NextPageContext} ctx
 */
async function getHeaders(ctx) {
  if (typeof window !== 'undefined') return null;
  if (typeof ctx.req === 'undefined') return null;

  const accessToken = getCookie(ctx, 'accessToken');

  return { Authorization: accessToken ? `Bearer ${accessToken}` : null };
}

/**
 * @param {{
 *  accessToken: string,
 * }} user
 */
function getUserToken(user) {
  if (!(user && user.accessToken)) return null;
  return { Authorization: `Bearer ${user.accessToken}` };
}

/**
 * Always creates a new apollo client on the server
 * Creates or reuses apollo client in the browser.
 * @param  {NormalizedCacheObject?} initialState
 * @param  {{ Authorization: string | null }?} headers
 * @param {string | number} [clientId]
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export const getApolloClient = (initialState, headers, clientId) => {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return createApolloClient(initialState, headers, clientId);
  }

  // Reuse client on the client-side
  if (!globalApolloClient || headers !== globalHeaders) {
    globalApolloClient = createApolloClient(initialState, headers, clientId);
    globalHeaders = headers;
  }

  return globalApolloClient;
};

/**
 * Creates a withApollo HOC
 * that provides the apolloContext
 * to a next.js Page or AppTree.
 * @param  {Object} withApolloOptions
 * @param  {Boolean} [withApolloOptions.ssr=false]
 * @returns {(PageComponent: React.ReactNode) => React.ReactNode}
 */
export const withApollo = ({ ssr = true } = {}) => (PageComponent) => {
  /**
   * @param {{
   *  pageProps?: {
   *   user: {
   *    accessToken: string
   *   },
   *   client: ApolloClient<NormalizedCacheObject>,
   *   apolloClient: ApolloClient<NormalizedCacheObject>,
   *   apolloState: NormalizedCacheObject,
   *  },
   *  apolloState: NormalizedCacheObject,
   * }} props
   */
  const WithApollo = (props) => {
    let client;
    /** @type {ApolloClient<NormalizedCacheObject>} */
    let apolloClient;
    /** @type {NormalizedCacheObject} */
    let apolloState;
    /** @type {props | {}} */
    let pageProps = {};

    if (props && props.pageProps) {
      ({ apolloClient, apolloState } = props.pageProps);
      pageProps = props;
    } else if (props) {
      ({ apolloState, ...pageProps } = props);
    }

    // @ts-ignore Variable 'apolloClient' is used before being assigned.
    if (apolloClient) {
      // Happens on: getDataFromTree & next.js ssr
      client = apolloClient;
    } else {
      // Happens on: next.js csr
      // client = initApolloClient(apolloState, undefined);
      // @ts-ignore TODO fix pageProps.user type
      client = getApolloClient(apolloState, getUserToken(pageProps.user));
    }

    return (
      <ApolloProvider client={client}>
        {/** @ts-ignore JSX element type 'PageComponent' does not have any construct or call signatures. */}
        <PageComponent {...pageProps} />
      </ApolloProvider>
    );
  };

  // @ts-ignore TODO fix PageComponent.getInitialProps type
  if (ssr || PageComponent.getInitialProps) {
    // @ts-ignore TODO fix ctx type
    WithApollo.getInitialProps = async (ctx) => {
      const { AppTree } = ctx;
      // Initialize ApolloClient, add it to the ctx object so
      // we can use it in `PageComponent.getInitialProp`.
      ctx.apolloClient = getApolloClient(null, await getHeaders(ctx.ctx));
      const { apolloClient } = ctx;

      // Run wrapped getInitialProps methods
      let pageProps = {};
      // @ts-ignore TODO fix PageComponent.getInitialProps type
      if (PageComponent.getInitialProps) {
        // @ts-ignore TODO fix PageComponent.getInitialProps type
        pageProps = await PageComponent.getInitialProps(ctx);
      }

      // Only on the server:
      if (typeof window === 'undefined') {
        // When redirecting, the response is finished.
        // No point in continuing to render
        if (ctx.res && ctx.res.finished) {
          return pageProps;
        }
        // Only if ssr is enabled
        if (ssr) {
          try {
            // Run all GraphQL queries
            // const { getDataFromTree } = await import('@apollo/react-ssr')
            await getDataFromTree(
              <AppTree
                pageProps={{
                  ...pageProps,
                  apolloClient
                }}
              />
            );
          } catch (error) {
            // Prevent Apollo Client GraphQL errors from crashing SSR.
            // Handle them in components via the data.error prop:
            // https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-query-data-error
            console.error('Error while running `getDataFromTree`', error);
          }
        }
      }

      // Extract query data from the Apollo store
      const apolloState = apolloClient.cache.extract();

      return {
        ...pageProps,
        apolloState
      };
    };
  }
  return WithApollo;
};
